import React, { PropsWithChildren, useCallback, useEffect, useMemo } from "react";
import { useState } from "react";
import ContentLoading from "./components/ContentLoading";
import { Guid } from "guid-typescript";
import { faSwatchbook } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Context {
  readonly isDark: boolean;
  readonly trigger?: string;
  readonly change: (dark: boolean) => void;
}

const ThemeContext = React.createContext<Context>(null!);

export function useTheme() {
  const { change, isDark } = React.useContext(ThemeContext);

  return { change, isDark };
}

export function ThemeProvider(props: PropsWithChildren) {
  const [loading, setLoading] = useState(false);
  const [trigger, setTrigger] = useState("");

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, 2500);

    return () => clearTimeout(timeoutId);
  }, [loading]);

  const change = useCallback((dark: boolean): void => {
    if (dark) {
      if (!window.document.documentElement.classList.contains("dark")) {
        window.document.documentElement.classList.add("dark");
      }
    } else {
      if (window.document.documentElement.classList.contains("dark")) {
        window.document.documentElement.classList.remove("dark");
      }
    }

    setLoading(true);
    setTrigger(Guid.create().toString());
  }, []);

  const isDark = window.document.documentElement.classList.contains("dark");

  const value = useMemo(
    () => ({ trigger, isDark, change }),
    [trigger, isDark, change],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ContentLoading
        loading={loading}
        title="Theme configuration.."
        icon={
          <FontAwesomeIcon
            className={`h-24 w-24 text-gray-500 opacity-80`}
            icon={faSwatchbook}
          />
        }
      >
        {props.children}
      </ContentLoading>
    </ThemeContext.Provider>
  );
}
